import { useState, useEffect } from 'react';
import { Popcorn } from 'lucide-react';
import { cn } from '../../lib/utils';
import { addToWatchlist, removeFromWatchlist, isInWatchlist } from '../../lib/supabase';
import { hapticSuccess } from '../../lib/haptics';

interface Props {
  tmdbId: number;
  title: string;
  year: number;
  posterUrl: string | null;
  overview: string;
  className?: string;
}

export function WatchlistButton({
  tmdbId,
  title,
  year,
  posterUrl,
  overview,
  className
}: Props) {
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkWatchlistStatus();
  }, [tmdbId]);

  const checkWatchlistStatus = async () => {
    try {
      const status = await isInWatchlist(tmdbId);
      setIsWatchlisted(status);
    } catch (err) {
      console.error('Error checking watchlist status:', err);
    }
  };

  const handleClick = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);

    try {
      if (isWatchlisted) {
        await removeFromWatchlist(tmdbId);
        setIsWatchlisted(false);
      } else {
        const result = await addToWatchlist({
          tmdb_id: tmdbId,
          title,
          year,
          poster_url: posterUrl,
          overview
        });
        if (result !== null) {
          setIsWatchlisted(true);
          void hapticSuccess();
        } else {
          // Movie already in watchlist (409 conflict)
          setIsWatchlisted(true);
        }
      }
    } catch (err) {
      console.error('Error updating watchlist:', err);
      if (err instanceof Error && err.message.includes('Authentication required')) {
        setError('Please sign in to add movies to your watchlist');
      } else if (err instanceof Error && err.message.includes('Network')) {
        setError('Connection issue. Please check your internet and try again');
      } else {
        setError('Unable to update watchlist. Please try again');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={cn(
          'rounded-full border p-2.5 transition-all duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          isWatchlisted
            ? 'border-primary/40 bg-primary/15 text-primary shadow-glow-gold-soft hover:bg-primary/20'
            : 'border-white/10 bg-white/[0.03] text-muted-foreground hover:border-primary/40 hover:text-primary',
          isLoading && 'cursor-not-allowed opacity-50',
          className
        )}
        title={isWatchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
      >
        <Popcorn className={cn(
          'h-5 w-5 transition-transform duration-200',
          isWatchlisted && 'scale-110',
          isLoading && 'animate-pulse'
        )} />
      </button>
      {error && (
        <div className="absolute bottom-full left-1/2 z-30 mb-2 w-48 -translate-x-1/2 rounded-lg border border-destructive/30 bg-popover p-2 text-xs text-red-300 shadow-deep">
          {error}
        </div>
      )}
    </div>
  );
}